<template>
  <section class="kpi-section">
    <!-- Animated Background Elements -->
    <div class="floating-shapes">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
    </div>

    <div class="max-w-7xl mx-auto px-6">
      <!-- Centered Stats Grid with Enhanced Design -->
      <div class="stats-container">
        <div class="stats-grid" data-aos="fade-up">
          <KpiCard
            v-for="(item, index) in stats"
            :key="index"
            :ref="'statItem' + index"
            :label="item.label"
            :value-text="item.currentValue"
            :progress="item.progress"
            :icon-type="getIconType(index)"
            :card-index="index"
            aos="fade-up"
          />
        </div>
      </div>

      <div class="content-grid mb-24">
        <div class="image-section" ref="imageContainer">
          <div class="image-wrapper">
            <!-- Animated gradient background -->
            <div class="bg-decoration"></div>
            <div class="bg-decoration-secondary"></div>

            <div class="image-container">
              <v-img
                data-aos="flip-up"
                :src="require('@/assets/images/AboutIt.jpg')"
                alt="Home Solutions"
                class="about-it-image"
                aspect-ratio="1.33"
                height="400"
              >
                <div class="image-overlay"></div>
              </v-img>

              <!-- Floating badge -->
              <div class="floating-badge">
                <svg class="badge-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                <span>Verified Partner</span>
              </div>
            </div>
          </div>
        </div>

        <div class="text-section" ref="textContainer">
          <div class="section-badge">
            <span class="badge-dot"></span>
            <span class="badge-text">Our Journey</span>
          </div>

          <div class="text-content">
            <p class="text-para">
              Since its launch in <span class="highlight highlight-primary">2024</span>, the
              <span class="highlight highlight-primary">Oh Yes Home Solution</span> app has rapidly
              gained traction, amassing over
              <span class="highlight highlight-accent">70,000 registered users</span> and achieving
              <span class="highlight highlight-accent">100,000+ downloads</span>.
            </p>
            <p class="text-para">
              The platform has successfully onboarded more than
              <span class="highlight highlight-success">3,000 experienced service partners</span>,
              establishing a robust network of professionals ready to provide a wide range of home
              solutions.
            </p>
          </div>

          <div class="highlight-box">
            <div class="highlight-box-bg"></div>
            <div class="icon-wrapper">
              <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                ></path>
              </svg>
              <div class="icon-pulse"></div>
            </div>
            <p class="highlight-box-text">
              With a strong foundation in smaller Indian cities, your expansion to larger cities and
              Chicago promises significant opportunities. This growth highlights the potential for
              wider reach and global market penetration.
            </p>

            <!-- Decorative corner elements -->
            <div class="corner-decoration corner-top-right"></div>
            <div class="corner-decoration corner-bottom-left"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import gsap from 'gsap';
import KpiCard from './KpiCard.vue';

export default {
  name: 'AboutIt',
  components: {
    KpiCard,
  },
  data() {
    return {
      stats: [
        { label: 'Downloads', value: 100000, currentValue: '0', progress: 0 },
        { label: 'Partners', value: 2000, currentValue: '0', progress: 0 },
      ],
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.animateContent();
      this.animateFloatingShapes();
    });
  },
  methods: {
    getIconType(index) {
      if (index === 0) return 'download';
      if (index === 1) return 'user';
      return 'partner';
    },
    animateFloatingShapes() {
      const shapes = document.querySelectorAll('.shape');
      shapes.forEach((shape, index) => {
        gsap.to(shape, {
          y: '+=30',
          x: '+=20',
          rotation: '+=10',
          duration: 3 + index,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: index * 0.5,
        });
      });
    },
    animateContent() {
      if (this.$refs.imageContainer) {
        gsap.fromTo(
          this.$refs.imageContainer,
          { x: -100, opacity: 0 },
          { x: 0, opacity: 1, duration: 1.2, ease: 'power3.out', delay: 0.2 }
        );
      }

      if (this.$refs.textContainer) {
        gsap.fromTo(
          this.$refs.textContainer,
          { x: 100, opacity: 0 },
          { x: 0, opacity: 1, duration: 1.2, ease: 'power3.out', delay: 0.2 }
        );
      }

      this.stats.forEach((stat, index) => {
        const el = this.$refs['statItem' + index];
        if (el && el.length > 0) {
          const cardElement = el[0].$el;

          gsap.fromTo(
            cardElement,
            { y: 100, opacity: 0, scale: 0.9 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              duration: 1,
              delay: 0.6 + index * 0.2,
              ease: 'back.out(1.4)',
            }
          );

          const obj = { count: 0 };
          gsap.to(obj, {
            count: stat.value,
            duration: 2.5,
            delay: 0.8 + index * 0.2,
            ease: 'power2.out',
            onUpdate: () => {
              this.stats[index].currentValue = Math.floor(obj.count).toLocaleString();
            },
          });

          gsap.to(this.stats[index], {
            progress: 100,
            duration: 2.5,
            delay: 0.8 + index * 0.2,
            ease: 'power2.out',
          });
        }
      });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap');

* {
  font-family: 'Nunito Sans', sans-serif;
  box-sizing: border-box;
}

.kpi-section {
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 50%, #f8f9fa 100%);
  position: relative;
  padding: 10px 0;
  overflow: hidden;
}

.kpi-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, transparent, #f46523, #ff7a45, transparent);
  box-shadow: 0 2px 12px rgba(244, 101, 35, 0.4);
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

/* Floating Background Shapes */
.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.08;
  filter: blur(60px);
}

.shape-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #f46523, #ff7a45);
  top: 10%;
  left: 5%;
}

.shape-2 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #ff7a45, #ffb347);
  bottom: 15%;
  right: 10%;
}

.shape-3 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #f46523, #ff6b35);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.max-w-7xl {
  max-width: 1280px;
}
.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
.mb-24 {
  margin-bottom: 4rem;
}

/* Centered Stats Container */
.stats-container {
  display: flex;
  justify-content: center;
  margin-bottom: 5rem;
  position: relative;
  z-index: 2;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 100%;
  width: 100%;
}

@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.content-grid {
  display: grid;
  gap: 4rem;
  align-items: center;
  margin-top: 40px;
  position: relative;
  z-index: 2;
}

@media (min-width: 1024px) {
  .content-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 5rem;
  }
}

/* Image Section */
.image-section {
  order: 2;
}
@media (min-width: 1024px) {
  .image-section {
    order: 1;
  }
}

.image-wrapper {
  position: relative;
}
.image-wrapper:hover .bg-decoration {
  transform: rotate(6deg) scale(1.08);
}
.image-wrapper:hover .bg-decoration-secondary {
  transform: rotate(-6deg) scale(1.05);
}
.image-wrapper:hover .image-container {
  transform: scale(1.03) translateY(-8px);
  box-shadow: 0 35px 70px rgba(244, 101, 35, 0.25);
}
.image-wrapper:hover .floating-badge {
  transform: translateY(-5px) scale(1.05);
}

.bg-decoration {
  position: absolute;
  inset: -10px;
  background: linear-gradient(135deg, #f46523 0%, #ff6b35 50%, #ff7a45 100%);
  border-radius: 2rem;
  transform: rotate(3deg);
  transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  opacity: 0.2;
  filter: blur(3px);
  animation: pulse 4s ease-in-out infinite;
}

.bg-decoration-secondary {
  position: absolute;
  inset: -5px;
  background: linear-gradient(225deg, #ff7a45 0%, #ffb347 100%);
  border-radius: 2rem;
  transform: rotate(-2deg);
  transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  opacity: 0.15;
  filter: blur(2px);
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.2;
  }
  50% {
    opacity: 0.3;
  }
}

.image-container {
  position: relative;
  border-radius: 2rem;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(244, 101, 35, 0.2);
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 4px solid #ffffff;
}

.about-it-image {
  width: 100%;
  height: 400px;
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(244, 101, 35, 0.2), transparent 60%);
}

/* Floating Badge */
.floating-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #fef5f1 100%);
  padding: 12px 20px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 8px 25px rgba(244, 101, 35, 0.25);
  border: 2px solid rgba(244, 101, 35, 0.3);
  font-weight: 700;
  color: #f46523;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.badge-icon {
  width: 18px;
  height: 18px;
  color: #10b981;
}

/* Section Badge */
.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #fef5f1 0%, #ffe5d9 100%);
  padding: 8px 20px;
  border-radius: 50px;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(244, 101, 35, 0.2);
}

.badge-dot {
  width: 8px;
  height: 8px;
  background: #f46523;
  border-radius: 50%;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.7;
  }
}

.badge-text {
  font-weight: 700;
  color: #f46523;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.text-section {
  order: 1;
}
@media (min-width: 1024px) {
  .text-section {
    order: 2;
  }
}

.text-content {
  margin-bottom: 2.5rem;
}
.text-para {
  color: #4a5568;
  line-height: 1.9;
  font-size: 1.125rem;
  margin-bottom: 1.5rem;
  font-weight: 400;
}

.highlight {
  font-weight: 700;
  position: relative;
  padding: 0 4px;
  transition: all 0.3s ease;
}

.highlight-primary {
  color: #f46523;
  background: linear-gradient(135deg, rgba(244, 101, 35, 0.1) 0%, rgba(255, 122, 69, 0.15) 100%);
  border-radius: 4px;
}

.highlight-accent {
  color: #ff6b35;
  background: linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(255, 179, 71, 0.15) 100%);
  border-radius: 4px;
}

.highlight-success {
  color: #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.15) 100%);
  border-radius: 4px;
}

.highlight-box {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #fef5f1 100%);
  border: 2px solid #ffe5d9;
  border-left: 6px solid #f46523;
  padding: 2.5rem 2.5rem 2.5rem 3.5rem;
  border-radius: 1.25rem;
  box-shadow: 0 15px 35px rgba(244, 101, 35, 0.1);
  transition: all 0.4s ease;
}

.highlight-box::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(135deg, rgba(244, 101, 35, 0.03) 0%, rgba(255, 122, 69, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.highlight-box:hover::before {
  opacity: 1;
}

.highlight-box:hover {
  transform: translateY(-6px);
  box-shadow: 0 25px 50px rgba(244, 101, 35, 0.15);
  border-left-width: 8px;
}

.highlight-box-bg {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(244, 101, 35, 0.05) 0%, transparent 70%);
  pointer-events: none;
}

.icon-wrapper {
  position: absolute;
  top: 2rem;
  left: 1.5rem;
  width: 3.5rem;
  height: 3.5rem;
  background: linear-gradient(135deg, #f46523, #ff7a45);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(244, 101, 35, 0.4);
  transition: all 0.4s ease;
  z-index: 2;
}

.icon-pulse {
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  border: 2px solid #f46523;
  opacity: 0;
  animation: pulse-ring 2s ease-out infinite;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.highlight-box:hover .icon-wrapper {
  transform: scale(1.15) rotate(15deg);
  box-shadow: 0 12px 30px rgba(244, 101, 35, 0.5);
}

.icon {
  width: 1.75rem;
  height: 1.75rem;
  color: white;
}

.highlight-box-text {
  color: #2d3748;
  font-weight: 600;
  line-height: 1.9;
  font-size: 1.125rem;
  position: relative;
  z-index: 10;
  margin-left: 2.5rem;
}

/* Corner Decorations */
.corner-decoration {
  position: absolute;
  width: 40px;
  height: 40px;
  border: 3px solid;
  opacity: 0.3;
  transition: all 0.4s ease;
}

.corner-top-right {
  top: 15px;
  right: 15px;
  border-color: transparent #f46523 #f46523 transparent;
  border-radius: 0 8px 0 0;
}

.corner-bottom-left {
  bottom: 15px;
  left: 15px;
  border-color: #ff7a45 transparent transparent #ff7a45;
  border-radius: 0 0 0 8px;
}

.highlight-box:hover .corner-decoration {
  opacity: 0.6;
  transform: scale(1.2);
}

@media (max-width: 768px) {
  .kpi-section {
    padding: 4rem 0;
  }
  .stats-grid {
    grid-template-columns: 1fr;
  }
  .content-grid {
    gap: 3rem;
  }
  .highlight-box {
    padding: 2rem 1.5rem 2rem 3rem;
  }
  .highlight-box-text {
    margin-left: 2rem;
    font-size: 1rem;
  }
  .floating-badge {
    padding: 10px 16px;
    font-size: 0.75rem;
  }
  .shape {
    filter: blur(40px);
  }
}
</style>
